import { Instant, LocalDateTime, ZoneId } from "@js-joda/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { API } from "../../../API";
import { STRINGS } from "../../../strings";
import { globalize, translate } from "../../../translate";
import { toTitleCase } from "../../../utils/stringUtils";
import { ModalActions, useModalV2 } from "../../AppModals";
import { PatientTaskInstanceDetails } from "../../../messages";
import { onTaskScheduleIsAlreadyTakenError } from "../../../utils/taskUtils";

const MAX_HOURS_IN_ADVANCE = 24;

export default function useScheduleTask(props: {
  api: API;
  taskBroadcast: PatientTaskInstanceDetails;
  scheduledDateTime: LocalDateTime | null;
  duration: number | null;
  onSuccessfulSchedule: () => void;
  onClickCallPatient: (scheduledDateTime: LocalDateTime) => void;
  onRequestGoBack: () => void;
}) {
  const modals = useModalV2();
  const queryClient = useQueryClient();

  const handleError = (error: any) => {
    modals.removeModal("schedule-confirmation-modal");
    modals.createModal({
      type: "error",
      title: translate(STRINGS.ERROR_SCHEDULING_TASK),
      message: onTaskScheduleIsAlreadyTakenError(error)
        ? translate(STRINGS.TASK_ALREADY_ASSIGNED)
        : translate(STRINGS.ERROR_SCHEDULING_TASK_MESSAGE),
      actions: [
        ModalActions.Close({
          onClick: () => modals.removeModal("error-scheduling-task"),
        }),
      ],
    });
  };

  const schedule = useMutation({
    mutationFn: (scheduledDateTime: LocalDateTime) =>
      props.api.updatePatientTaskInstanceScheduleDateTime(props.taskBroadcast.taskInstanceId, {
        scheduleDateTime: scheduledDateTime.atZone(ZoneId.systemDefault()).toInstant(),
        duration: props.duration ?? 0,
      }),
    onMutate: () => {
      modals.updateModal("schedule-confirmation-modal", {
        actions: [
          {
            key: "schedule",
            children: translate(STRINGS.SCHEDULE),
            isLoading: true,
            onClick: () =>
              props.scheduledDateTime !== null && schedule.mutate(props.scheduledDateTime),
          },
          ModalActions.Close(),
        ],
      });
    },
    onSuccess: () => {
      props.onSuccessfulSchedule();
      modals.removeModal("schedule-confirmation-modal");
      modals.createModal({
        type: "success",
        title: translate(STRINGS.TASK_SCHEDULED),
        message: translate(STRINGS.TASK_SCHEDULED_MESSAGE),
        actions: [
          ModalActions.Close({
            onClick: () => props.onRequestGoBack(),
          }),
        ],
      });
      queryClient.invalidateQueries(["time-slots"]);
    },
    onError: (error) => handleError(error),
    onSettled: () => {
      modals.updateModal("schedule-confirmation-modal", {
        actions: [
          {
            key: "schedule",
            children: translate(STRINGS.SCHEDULE),
            onClick: () =>
              props.scheduledDateTime !== null && schedule.mutate(props.scheduledDateTime),
          },
          ModalActions.Close(),
        ],
      });
    },
  });

  const lateSchedule = useMutation({
    mutationFn: (scheduledDateTime: LocalDateTime) =>
      props.api.schedulePatientTaskInstance(props.taskBroadcast.taskInstanceId, {
        scheduledDateTime,
        patientConfirmedSchedule: true,
      }),
    onSuccess: (_, scheduledDateTime) => {
      props.onSuccessfulSchedule();
      modals.removeModal("schedule-confirmation-modal");
      const dateTimeStr = globalize().formatLocalDateTime(scheduledDateTime, { datetime: "short" });

      modals.createModal({
        type: "success",
        title: translate(STRINGS.TASK_SCHEDULED),
        message: translate(STRINGS.YOUR_TASK_HAS_BEEN_SCHEDULED_FOR).replace(
          "{{dateTime}}",
          dateTimeStr
        ),
        actions: [
          ModalActions.Close({
            onClick: () => props.onRequestGoBack(),
          }),
        ],
      });

      queryClient.invalidateQueries(["time-slots"]);
    },
    onError: (error) => handleError(error),
  });

  const openModal = (datetime: LocalDateTime) => {
    const zoneId = ZoneId.of("America/New_York");
    const now = Instant.now().atZone(zoneId).toLocalDateTime();

    if (datetime.isBefore(now.plusHours(MAX_HOURS_IN_ADVANCE))) {
      openLateScheduleModal(datetime);
    } else {
      openEarlyScheduleModal(datetime);
    }
  };

  const openLateScheduleModal = (datetime: LocalDateTime) => {
    const dateTimeStr = globalize().formatLocalDateTime(datetime, { datetime: "short" });
    const patientName = props.taskBroadcast.patientName;

    const modal = modals.createModal({
      id: "call-patient-modal",
      type: "info",
      title: toTitleCase(translate(STRINGS.CALL_PATIENT)),
      message: translate(STRINGS.CALL_PATIENT_CONFIRMATION)
        .replace("{{patientName}}", patientName)
        .replace("{{dateTime}}", dateTimeStr)
        .replace("{{hours}}", MAX_HOURS_IN_ADVANCE.toString()),
      actions: [
        {
          key: "call-patient",
          children: translate(STRINGS.CALL_PATIENT),
          onClick: () => {
            props.onClickCallPatient(datetime);
            modal.remove();
          },
        },
        ModalActions.Close({ children: translate(STRINGS.CANCEL), onClick: () => modal.remove() }),
      ],
    });
  };

  const openEarlyScheduleModal = (datetime: LocalDateTime) => {
    const dateTimeStr = globalize().formatLocalDateTime(datetime, { datetime: "short" });

    const modal = modals.createModal({
      id: "schedule-confirmation-modal",
      type: "info",
      title: translate(STRINGS.SCHEDULE_TASK),
      message: translate(STRINGS.SCHEDULE_CONFIRMATION).replace("{{dateTime}}", dateTimeStr),
      actions: [
        {
          key: "schedule",
          children: translate(STRINGS.SCHEDULE),
          onClick: () =>
            props.scheduledDateTime !== null && schedule.mutate(props.scheduledDateTime),
        },
        ModalActions.Close({ children: translate(STRINGS.CANCEL), onClick: () => modal.remove() }),
      ],
    });
  };

  return {
    openModal,
    lateSchedule,
  };
}
